import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jobServices } from "../../services/jobServices";
import { invoiceServices } from "../../services/invoiceServices";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, CircularProgress, FormControl, Grid2, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, TableFooter, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { useSnackbar } from "../../utils/useSnackbar";
import PageHeader from "../ui/PageHeader";
import ActionProgressDialog from '../common/ActionProgressDialog';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import "../../css/Invoice.css";
import "../../css/Dialog.css";

const InvoiceForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [invoiceType, setInvoiceType] = useState("Adhoc Billing");
    const [invoiceCountry, setInvoiceCountry] = useState("INDIA");
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [invoiceIdToDelete, setInvoiceIdToDelete] = useState(null);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
    const [downloadingType, setDownloadingType] = useState(null);
    const [pastInvoices, setPastInvoices] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [newFteType, setNewFteType] = useState("");
    const [newFteQty, setNewFteQty] = useState(1);
    const [newFtePrice, setNewFtePrice] = useState("");
    const [newFteTimeType, setNewFteTimeType] = useState("Full Time");
    const [openOtherDialog, setOpenOtherDialog] = useState(false);
    const [customServiceName, setCustomServiceName] = useState("");
    const [isEditingOtherService, setIsEditingOtherService] = useState(false);
    const [editingRowIndex, setEditingRowIndex] = useState(null);

    const FTE_SERVICE_TYPES = [
        { value: "Bookkeeper", label: "Bookkeeper" },
        { value: "Accountant", label: "Accountant" },
        { value: "Other", label: "Other" },
    ];

    const TIME_TYPE_OPTIONS = [
        { value: "Full Time", label: "Full Time" },
        { value: "Half Time", label: "Half Time" }
    ];

    const isCustomService = (name) => {
        if (!name) return false;
        return !FTE_SERVICE_TYPES.some(t => t.value === name);
    };

    const getServiceDropdownValue = (name) => {
        if (!name) return "";
        if (isCustomService(name)) {
            return "Other";
        }
        return name;
    };

    const getDefaultRate = (jobType) => {
        if (!selectedClientId || !jobType) return 0;
        const client = clients.find(c => c.id === selectedClientId);
        if (client && client.jobRates) {
            const rateObj = client.jobRates.find(r => r.jobType === jobType);
            return rateObj ? parseFloat(rateObj.rate) : 0;
        }
        return 0;
    };

    const formatChargeHoursToHHMM = (decimalHours) => {
        if (!decimalHours) return "00:00";
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
        if (location.state && location.state.invoiceDetails) {
            setSelectedClientId(location.state.clientId);
            setStartDate(location.state.startDate);
            setEndDate(location.state.endDate);
            setInvoiceType(location.state.invoiceType);
            setInvoiceCountry(location.state.country);
            setJobs(location.state.invoiceDetails);
        }
    }, [location]);

    useEffect(() => {
        const loadClients = async () => {
            try {
                const data = await jobServices.getClientsDataInJob();
                setClients(data);
            } catch (err) {
                showSnackbar("Failed to load clients", "error");
            }
        };
        loadClients();
    }, []);

    useEffect(() => {
        let total = 0;
        jobs.forEach(job => {
            const rate = parseFloat(job.hourlyRate) || 0;
            const hours = parseFloat(job.chargeHours) || 0;
            job.totalRate = (rate * hours).toFixed(2);
            total += (rate * hours);
        });
        setTotalAmount(total.toFixed(2));
    }, [jobs]);

    const handleOpenHistory = async () => {
        if (!selectedClientId) {
            showSnackbar("Please select a client first.", "error");
            return;
        }
        setLoadingHistory(true);
        setOpenHistoryDialog(true);
        try {
            const data = await invoiceServices.getInvoicesByClientId(selectedClientId);
            setPastInvoices(data);
        } catch (err) {
            showSnackbar("Failed to fetch invoice history", "error");
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleHistoryPreview = (invoice) => {
        const client = clients.find(c => c.id === invoice.clientId);
        const clientName = client ? (client.clientName || client.name) : "";
        const clientAddress = client ? client?.address : "";
        const contactPersonName = client ? client?.contactPersonName : "";

        navigate("/home/invoices/preview", {
            state: {
                id: invoice.id,
                clientId: invoice.clientId,
                clientName: clientName,
                contactPersonName: contactPersonName,
                clientAddress: clientAddress,
                startDate: invoice.startDate,
                endDate: invoice.endDate,
                invoiceType: invoice.invoiceType,
                invoiceDetails: invoice.invoiceDetails,
                country: invoice.country
            }
        });
    };

    const handleHistoryDownload = async (invoice) => {
        setDownloadingInvoiceId(invoice.id);
        setDownloadingType('pdf');

        const selectedClient = clients.find(c => c.id === selectedClientId);
        const clientName = selectedClient ? (selectedClient.clientName || selectedClient.name) : "";
        const contactPersonName = selectedClient ? selectedClient.contactPersonName : "";
        const clientAddress = selectedClient ? selectedClient?.address : "";

        const payload = {
            id: invoice.id,
            clientId: invoice.clientId,
            clientName: clientName,
            contactPersonName: contactPersonName,
            clientAddress: clientAddress,
            startDate: invoice.startDate,
            endDate: invoice.endDate,
            invoiceType: invoice.invoiceType,
            invoiceDetails: invoice.invoiceDetails,
            country: invoice.country
        };

        try {
            await invoiceServices.downloadInvoice(payload);
            showSnackbar("PDF download started", "success");
        } catch (err) {
            showSnackbar("Failed to download PDF invoice", "error");
        } finally {
            setDownloadingInvoiceId(null);
            setDownloadingType(null);
        }
    };

    const handleHistoryDownloadExcel = async (invoice) => {
        setDownloadingInvoiceId(invoice.id);
        setDownloadingType('excel');

        const selectedClient = clients.find(c => c.id === selectedClientId);
        const clientName = selectedClient ? (selectedClient.clientName || selectedClient.name) : "";
        const contactPersonName = selectedClient ? selectedClient.contactPersonName : "";
        const clientAddress = selectedClient ? selectedClient?.address : "";

        const payload = {
            id: invoice.id,
            clientId: invoice.clientId,
            clientName: clientName,
            contactPersonName: contactPersonName,
            clientAddress: clientAddress,
            startDate: invoice.startDate,
            endDate: invoice.endDate,
            invoiceType: invoice.invoiceType,
            invoiceDetails: invoice.invoiceDetails,
            country: invoice.country
        };

        try {
            await invoiceServices.downloadInvoiceExcel(payload);
            showSnackbar("Excel download started", "success");
        } catch (err) {
            showSnackbar("Failed to download Excel invoice", "error");
        } finally {
            setDownloadingInvoiceId(null);
            setDownloadingType(null);
        }
    };

    const handleHistoryDelete = async (invoiceId) => {
        setInvoiceIdToDelete(invoiceId);
        setOpenDialog(true);
    };

    const confirmDelete = async () => {
        try {
            await invoiceServices.deleteInvoiceById(invoiceIdToDelete);
            setOpenDialog(false);
            setInvoiceIdToDelete(null);
            const data = await invoiceServices.getInvoicesByClientId(selectedClientId);
            setPastInvoices(data);
            showSnackbar("Invoice deleted successfully", "success");
        } catch (err) {
            showSnackbar("Failed to delete invoice", "error");
            setOpenDialog(false);
        }
    };

    const handleFetchJobs = async () => {
        if (!selectedClientId || !startDate || !endDate) {
            showSnackbar("Please select Client, Start Date, and End Date.", "error");
            return;
        }
        if (invoiceType !== "FTE" && startDate > endDate) {
            showSnackbar("Start date must be earlier than end date.", "error");
            return;
        }

        setLoading(true);
        try {
            const exists = await invoiceServices.checkInvoiceExists(selectedClientId, startDate, endDate, location.state?.id);
            if (exists) {
                showSnackbar("An invoice already exists for this client with the selected start and end date.", "error");
                setJobs([]);
                setLoading(false);
                return;
            }

            const data = await invoiceServices.getClientJobs(selectedClientId, startDate, endDate);

            const initializedJobs = data.map(job => {
                const defaultRate = getDefaultRate(job.type);
                const hours = parseFloat(job.chargeHours) || 0;
                return {
                    ...job,
                    hourlyRate: defaultRate,
                    totalRate: (defaultRate * hours).toFixed(2)
                };
            });
            setJobs(initializedJobs);

            if (data.length === 0) {
                showSnackbar("No jobs found for this date range.", "info");
            }
        } catch (err) {
            showSnackbar("Failed to fetch jobs. Please check the date range.", "error");
            setJobs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleServiceTypeChange = (value) => {
        if (value === "Other") {
            setIsEditingOtherService(false);
            setCustomServiceName("");
            setOpenOtherDialog(true);
            setNewFtePrice("");
        } else {
            setNewFteType(value);
            let rate = getDefaultRate(value);
            if (newFteTimeType === "Half Time") {
                rate = rate / 2;
            }
            setNewFtePrice(rate);
        }
    };

    const handleNewTimeTypeChange = (value) => {
        setNewFteTimeType(value);

        // Recalculate price if a service is already selected
        if (newFteType && !isCustomService(newFteType)) {
            let rate = getDefaultRate(newFteType);
            if (value === "Half Time") {
                rate = rate / 2;
            }
            setNewFtePrice(rate);
        }
    };

    // Handler for service type change in table row
    const handleTableServiceTypeChange = (index, value) => {
        if (value === "Other") {
            setIsEditingOtherService(true);
            setEditingRowIndex(index);

            if (isCustomService(jobs[index].name)) {
                setCustomServiceName(jobs[index].name);
            } else {
                setCustomServiceName("");
            }
            setOpenOtherDialog(true);
        } else {
            const updated = [...jobs];
            updated[index].name = value;

            let newRate = getDefaultRate(value);

            if (updated[index].timeType === "Half Time") {
                newRate = newRate / 2;
            }
            updated[index].hourlyRate = newRate;

            const qty = parseFloat(updated[index].chargeHours) || 0;
            updated[index].totalRate = (newRate * qty).toFixed(2);

            setJobs(updated);
        }
    };

    const handleEditCustomServiceName = (index) => {
        setIsEditingOtherService(true);
        setEditingRowIndex(index);
        setCustomServiceName(jobs[index].name);
        setOpenOtherDialog(true);
    };

    const handleEditNewFteCustomName = () => {
        setIsEditingOtherService(false);
        setCustomServiceName(newFteType);
        setOpenOtherDialog(true);
    };

    const handleOtherServiceConfirm = () => {
        if (!customServiceName.trim()) {
            showSnackbar("Please enter a service name.", "error");
            return;
        }

        if (isEditingOtherService && editingRowIndex !== null) {
            const updated = [...jobs];
            updated[editingRowIndex].name = customServiceName.trim();

            const rate = getDefaultRate(customServiceName.trim());
            if (rate > 0) {
                updated[editingRowIndex].hourlyRate = rate;
                const qty = parseFloat(updated[editingRowIndex].chargeHours) || 0;
                updated[editingRowIndex].totalRate = (rate * qty).toFixed(2);
            }

            setJobs(updated);
            setEditingRowIndex(null);
            setIsEditingOtherService(false);
        } else {
            setNewFteType(customServiceName.trim());

            const rate = getDefaultRate(customServiceName.trim());
            setNewFtePrice(rate);
        }

        setCustomServiceName("");
        setOpenOtherDialog(false);
    };

    // Handler for closing the "Other" dialog without saving
    const handleOtherDialogClose = () => {
        const wasEditing = isEditingOtherService;

        setCustomServiceName("");
        setOpenOtherDialog(false);
        setEditingRowIndex(null);
        setIsEditingOtherService(false);

        if (!wasEditing) {
            setNewFteType("");
        }
    };

    const handleCustomNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleOtherServiceConfirm();
        }
    };

    const handleAddFteItem = () => {
        if (!newFteType) {
            showSnackbar("Please select a Job Type.", "error");
            return;
        }
        if (!newFteQty || newFteQty <= 0) {
            showSnackbar("Quantity must be greater than 0.", "error");
            return;
        }
        if (!newFtePrice || newFtePrice <= 0) {
            showSnackbar("Please enter a valid Price.", "error");
            return;
        }

        const total = (parseFloat(newFteQty) * parseFloat(newFtePrice)).toFixed(2);

        const newItem = {
            id: Date.now(),
            name: newFteType,
            type: "FTE",
            timeType: newFteTimeType,
            chargeHours: parseFloat(newFteQty),
            hourlyRate: parseFloat(newFtePrice),
            totalRate: total
        };

        setJobs([...jobs, newItem]);
        setNewFteType("");
        setNewFteTimeType("Full Time");
        setNewFteQty(1);
        setNewFtePrice("");
    };

    const handleTimeTypeChange = (index, value) => {
        const updatedJobs = [...jobs];
        updatedJobs[index].timeType = value;

        const jobName = updatedJobs[index].name;
        let calculatedRate = getDefaultRate(jobName);

        if (value === "Half Time") {
            calculatedRate = calculatedRate / 2;
        }

        updatedJobs[index].hourlyRate = calculatedRate;

        const qty = parseFloat(updatedJobs[index].chargeHours) || 0;
        updatedJobs[index].totalRate = (calculatedRate * qty).toFixed(2);

        setJobs(updatedJobs);
    };

    const handleRateChange = (index, field, value) => {
        const updatedJobs = [...jobs];

        if (field === 'rate') {
            updatedJobs[index].hourlyRate = value;
        } else if (field === 'qty') {
            updatedJobs[index].chargeHours = value;
        }

        const qty = parseFloat(updatedJobs[index].chargeHours) || 0;
        const rate = parseFloat(updatedJobs[index].hourlyRate) || 0;
        updatedJobs[index].totalRate = (qty * rate).toFixed(2);

        setJobs(updatedJobs);
    };

    const handleRemoveService = (index) => {
        const updatedJobs = jobs.filter((_, i) => i !== index);
        setJobs(updatedJobs);
    };

    const handlePreview = async () => {
        if (jobs.length === 0) {
            showSnackbar("No jobs to preview.", "error");
            return;
        }

        if (invoiceType !== "FTE" && startDate && endDate) {
            try {
                const exists = await invoiceServices.checkInvoiceExists(selectedClientId, startDate, endDate, location.state?.id);
                if (exists) {
                    showSnackbar("An invoice already exists for this client with the selected start and end date.", "error");
                    return;
                }
            } catch (err) {
                // Let backend validation catch it if check fails
            }
        }

        const selectedClient = clients.find(c => c.id === selectedClientId);
        const clientName = selectedClient ? (selectedClient.clientName || selectedClient.name) : "";
        const contactPersonName = selectedClient ? selectedClient?.contactPersonName : "";
        const clientAddress = selectedClient ? selectedClient?.address : "";

        const payload = {
            id: location.state?.id || null,
            clientId: selectedClientId,
            clientName: clientName,
            contactPersonName: contactPersonName,
            clientAddress: clientAddress,
            startDate: startDate,
            endDate: endDate,
            invoiceType: invoiceType,
            invoiceDetails: jobs,
            country: invoiceCountry
        };

        navigate("/home/invoices/preview", { state: payload });
    };

    const resetForm = () => {
        setSelectedClientId("");
        setStartDate("");
        setEndDate("");
        setInvoiceType("");
        setJobs([]);
        setTotalAmount(0);

        navigate(location.pathname, { replace: true, state: null });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const [datePart] = dateString.split(" ");
        const parts = datePart.split("-");
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                const [year, month, day] = parts;
                return `${day}/${month}/${year}`;
            }
            const [day, month, year] = parts;
            return `${day}/${month}/${year}`;
        }
        return dateString;
    };

    return (
        <Box className="invoice-page-container">
            <PageHeader title={t('Invoices')} />

            <Box className="invoice-filter-section">
                <Box sx={{ display: 'flex', gap: 2, flex: 1, minWidth: { xs: 0, md: 300 }, flexWrap: 'nowrap' }}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel>Client</InputLabel>
                        <Select
                            value={selectedClientId}
                            label="Client"
                            onChange={(e) => {
                                setSelectedClientId(e.target.value);
                                setJobs([]);
                                setStartDate("");
                                setEndDate("");
                            }}
                            sx={{ borderRadius: 3 }}
                        >
                            {clients.map(client => (
                                <MenuItem key={client.id} value={client.id}>
                                    {client.clientName || client.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Tooltip title="View Past Invoices">
                        <IconButton
                            onClick={handleOpenHistory}
                            disabled={!selectedClientId}
                            color="primary"
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 3,
                                width: 56,
                                height: 56
                            }}
                        >
                            <HistoryRoundedIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                <FormControl sx={{ minWidth: { xs: 0, md: 200 }, width: { xs: '100%', sm: 'auto' } }} variant="outlined">
                    <InputLabel>Invoice Type</InputLabel>
                    <Select
                        value={invoiceType}
                        label="Invoice Type"
                        onChange={(e) => {
                            setInvoiceType(e.target.value)
                            setJobs([]);
                            setNewFteType("");
                            setNewFtePrice("");
                        }}
                        sx={{ borderRadius: 3 }}
                    >
                        <MenuItem value="Adhoc Billing">Adhoc Billing</MenuItem>
                        <MenuItem value="FTE">FTE Billing</MenuItem>
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: { xs: 0, md: 150 }, width: { xs: '100%', sm: 'auto' } }} variant="outlined">
                    <InputLabel>Country</InputLabel>
                    <Select
                        value={invoiceCountry}
                        label="Country"
                        onChange={(e) => setInvoiceCountry(e.target.value)}
                        sx={{ borderRadius: 3 }}
                    >
                        <MenuItem value="INDIA">India</MenuItem>
                        <MenuItem value="UK">United Kingdom</MenuItem>
                    </Select>
                </FormControl>

                {invoiceType !== "FTE" && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
                        <TextField
                            type="date"
                            label="Start Date"
                            variant="outlined"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 }, minWidth: { xs: '100%', sm: 180 } }}
                        />
                        <TextField
                            type="date"
                            label="End Date"
                            variant="outlined"
                            value={endDate}
                            disabled={!startDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 }, minWidth: { xs: '100%', sm: 180 } }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleFetchJobs}
                            disabled={!selectedClientId || !startDate || !endDate}
                            sx={{ borderRadius: 3, px: 4, width: { xs: '100%', sm: 'auto' } }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Continue"}
                        </Button>
                    </Box>
                )}
            </Box>

            {invoiceType === "FTE" && (
                <Paper sx={{ p: 3, mb: 3, boxShadow: 1, border: '1px dashed #ccc', bgcolor: '#fafafa' }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Add Services</Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>

                        <FormControl sx={{ minWidth: 200 }} variant="outlined" size="small">
                            <InputLabel>Services</InputLabel>
                            <Select
                                value={getServiceDropdownValue(newFteType)}
                                label="Services"
                                onChange={(e) => handleServiceTypeChange(e.target.value)}
                            >
                                {FTE_SERVICE_TYPES.map(type => (
                                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {isCustomService(newFteType) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 }
                                }}
                                onClick={handleEditNewFteCustomName}
                            >
                                <Typography variant="body2" sx={{ color: 'primary.main', fontStyle: 'italic' }}>
                                    ({newFteType})
                                </Typography>
                                <Tooltip title="Edit name">
                                    <EditIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                                </Tooltip>
                            </Box>
                        )}

                        <FormControl sx={{ minWidth: 120 }} variant="outlined" size="small">
                            <InputLabel>Time Type</InputLabel>
                            <Select
                                value={newFteTimeType}
                                label="Time Type"
                                onChange={(e) => handleNewTimeTypeChange(e.target.value)}
                            >
                                {TIME_TYPE_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Quantity"
                            type="number"
                            variant="outlined"
                            size="small"
                            sx={{ width: 100 }}
                            value={newFteQty}
                            onChange={(e) => setNewFteQty(e.target.value)}
                            slotProps={{
                                htmlInput: { min: 1, },
                            }}
                        />

                        <TextField
                            label="Price"
                            type="number"
                            variant="outlined"
                            size="small"
                            sx={{ width: 150 }}
                            value={newFtePrice}
                            onChange={(e) => setNewFtePrice(e.target.value)}
                            slotProps={{
                                htmlInput: { min: 0, step: 1, },
                            }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<AddCircleOutlineRoundedIcon />}
                            size="small"
                            onClick={handleAddFteItem}
                            disabled={!newFteType || !newFtePrice}
                            sx={{
                                height: "2.5rem",
                            }}
                        >
                            Add
                        </Button>
                    </Box>
                </Paper>
            )}

            {jobs.length > 0 &&

                <Box className="invoice-table-card">
                    <Box className="invoice-table-header">
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptRoundedIcon /> {invoiceType === "FTE" ? "Service Items" : "Billable Jobs"}
                        </Typography>
                    </Box>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>
                                        {invoiceType === "FTE" ? "Services" : "Job Name"}
                                    </TableCell>

                                    {invoiceType === "FTE" && (
                                        <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>Time Type</TableCell>
                                    )}

                                    {invoiceType !== "FTE" && (
                                        <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>Job Type</TableCell>
                                    )}

                                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>
                                        {invoiceType === "FTE" ? "Quantity" : "Hours"}
                                    </TableCell>

                                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>
                                        {invoiceType === "FTE" ? "Price" : "Hourly Rate"}
                                    </TableCell>

                                    {invoiceType === "FTE" && (
                                        <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>Actions</TableCell>
                                    )}

                                    <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobs.map((job, index) => (
                                    <TableRow key={job.id} hover>
                                        <TableCell align="left">
                                            {invoiceType === "FTE" ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Select
                                                        value={getServiceDropdownValue(job.name)}
                                                        size="small"
                                                        fullWidth
                                                        onChange={(e) => handleTableServiceTypeChange(index, e.target.value)}
                                                        sx={{ flex: 1, borderRadius: 2 }}
                                                    >
                                                        {FTE_SERVICE_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                                    </Select>
                                                    {isCustomService(job.name) && (
                                                        <Tooltip title="Edit name">
                                                            <IconButton size="small" onClick={() => handleEditCustomServiceName(index)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{job.name}</Typography>
                                            )}
                                        </TableCell>

                                        {invoiceType === "FTE" && (
                                            <TableCell align="center">
                                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                                    <Select
                                                        value={job.timeType || "Full Time"}
                                                        onChange={(e) => handleTimeTypeChange(index, e.target.value)}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        {TIME_TYPE_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                        )}

                                        {invoiceType !== "FTE" && <TableCell align="center">{job.type}</TableCell>}

                                        <TableCell align="center">
                                            {invoiceType === "FTE" ? (
                                                <TextField
                                                    type="number"
                                                    size="small"
                                                    variant="outlined"
                                                    value={job.chargeHours}
                                                    onChange={(e) => handleRateChange(index, 'qty', e.target.value)}
                                                    slotProps={{ input: { min: 1, style: { textAlign: 'center' } } }}
                                                    sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            ) : (
                                                formatChargeHoursToHHMM(job.chargeHours)
                                            )}
                                        </TableCell>

                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                size="small"
                                                variant="outlined"
                                                value={job.hourlyRate}
                                                onChange={(e) => handleRateChange(index, 'rate', e.target.value)}
                                                slotProps={{ htmlInput: { min: 0, step: 1, style: { textAlign: 'center' } } }}
                                                sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                            />
                                        </TableCell>

                                        {invoiceType === "FTE" && (
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveService(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}

                                        <TableCell align="center">
                                            <Typography variant="body1" sx={{ fontWeight: 800, color: 'var(--primary)' }}>
                                                £{parseFloat(job.totalRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box className="invoice-summary-card">
                        <Box className="invoice-total-box">
                            <Box className="invoice-total-row invoice-grand-total">
                                <Typography>Grand Total</Typography>
                                <Typography>£{totalAmount}</Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box className="invoice-actions" sx={{ mb: 4, mr: { xs: 0, md: 2 }, gap: 2 }}>
                        <Button variant="outlined" color="error" onClick={resetForm} sx={{ borderRadius: 999, px: 4, width: { xs: '100%', sm: 'auto' } }}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handlePreview} disabled={jobs.length === 0} sx={{ borderRadius: 999, px: 6, width: { xs: '100%', sm: 'auto' } }}>
                            Preview Invoice
                        </Button>
                    </Box>
                </Box>
            }

            {/* Dialog for entering/editing custom "Other" service name */}
            <Dialog
                open={openOtherDialog}
                onClose={handleOtherDialogClose}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { className: 'modern-dialog' } }}
            >
                <DialogTitle className="modern-dialog-title">
                    <span className="modern-dialog-title-text">
                        {isEditingOtherService ? "Edit Service Name" : "Enter Service Name"}
                    </span>
                    <IconButton
                        aria-label="close"
                        onClick={handleOtherDialogClose}
                        className="modern-dialog-close-btn"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="modern-dialog-content">
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', mt: 1 }}>
                        {isEditingOtherService
                            ? "Update the name for this custom service."
                            : "Please enter a name for this custom service."
                        }
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        variant="outlined"
                        label="Service Name"
                        value={customServiceName}
                        onChange={(e) => setCustomServiceName(e.target.value)}
                        onKeyDown={handleCustomNameKeyDown}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions className="modern-dialog-actions">
                    <Button
                        onClick={handleOtherDialogClose}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleOtherServiceConfirm}
                        variant="contained"
                        disabled={!customServiceName.trim()}
                        disableElevation
                    >
                        {isEditingOtherService ? "Update" : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openHistoryDialog}
                onClose={() => setOpenHistoryDialog(false)}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { className: 'modern-dialog' } }}
            >
                <DialogTitle className="modern-dialog-title">
                    <span className="modern-dialog-title-text">Client Invoices</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenHistoryDialog(false)}
                        className="modern-dialog-close-btn"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="modern-dialog-content">
                    {loadingHistory ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : pastInvoices.length > 0 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                            {pastInvoices.map((inv) => (
                                <Paper
                                    key={inv.id}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        justifyContent: "space-between",
                                        alignItems: { xs: "flex-start", sm: "center" },
                                        gap: { xs: 2, sm: 0 },
                                        borderRadius: 3,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            boxShadow: "0 2px 0.5rem rgba(0,0,0,0.05)",
                                            borderColor: "primary.light"
                                        }
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                            <Typography fontWeight={700} color="primary.main">
                                                {inv.invoiceNumber}
                                            </Typography>
                                            <Typography variant="caption" sx={{ px: 1, py: 0.2, borderRadius: 1, bgcolor: inv.invoiceType === 'FTE' ? '#eff6ff' : '#f0fdf4', color: inv.invoiceType === 'FTE' ? '#1d4ed8' : '#15803d', fontWeight: 600 }}>
                                                {inv.invoiceType}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                                            {inv.startDate && inv.endDate && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Period: <Typography component="span" variant="body2" fontWeight={600} color="text.primary">{formatDate(inv.startDate)} - {formatDate(inv.endDate)}</Typography>
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(inv.createdAt)}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700}>
                                                £{inv.totalAmount}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" }, flexWrap: 'wrap' }}>
                                        <Button
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleHistoryPreview(inv);
                                            }}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={downloadingInvoiceId === inv.id && downloadingType === 'pdf' ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleHistoryDownload(inv);
                                            }}
                                            disabled={downloadingInvoiceId !== null}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            PDF
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={downloadingInvoiceId === inv.id && downloadingType === 'excel' ? <CircularProgress size={16} color="inherit" /> : <TableChartIcon />}
                                            variant="outlined"
                                            color="success"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleHistoryDownloadExcel(inv);
                                            }}
                                            disabled={downloadingInvoiceId !== null}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            Excel
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            variant="outlined"
                                            color="error"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleHistoryDelete(inv.id);
                                            }}
                                            sx={{ borderRadius: 2, flex: { xs: 1, sm: "none" }, minWidth: { xs: 0, sm: 'auto' } }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ py: 4, textAlign: 'center', opacity: 0.6 }}>
                            <Typography>No past invoices found for this client.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={confirmDelete}
                title="Delete Confirmation"
                message="Are you sure you want to delete this invoice?"
                messageSecond="*This action cannot be undone."
                cancelLabel="Cancel"
                confirmLabel="Delete"
                isDelete={true}
            />
            {SnackbarComponent}
            <ActionProgressDialog
                open={downloadingInvoiceId !== null}
                message={downloadingType === 'excel' ? "Downloading Excel Invoice..." : "Downloading PDF Invoice..."}
            />
        </Box>
    );
};

export default InvoiceForm;